
import React, { useState } from 'react';
import { Bot, Clock, Eye } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Bot as BotType } from '@/types/trader';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EditBotDialog } from './EditBotDialog';
import { useTranslation } from 'react-i18next';

interface BotInfoCardProps {
  bot: BotType | null | undefined;
  isLoading: boolean;
}

export const BotInfoCard = ({ bot, isLoading }: BotInfoCardProps) => {
  const { t } = useTranslation();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle><Skeleton className="h-6 w-24" /></CardTitle>
          <CardDescription><Skeleton className="h-4 w-32" /></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>
    );
  }

  if (!bot) return null;

  const exchanges = Array.isArray(bot.exchange) ? bot.exchange : [bot.exchange].filter(Boolean);
  const truncatedBotId = bot.bot_id ? bot.bot_id.substring(0, 8) : '';
  const isLongDescription = bot.description && bot.description.length > 100;
  const truncatedDescription = isLongDescription 
    ? `${bot.description.substring(0, 100)}...` 
    : bot.description;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot size={18} />
            {t("bot_information")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1 text-muted-foreground text-sm">{t('name')}:</div>
            <div className="col-span-2 font-medium">{bot.name}</div>
            <div className="col-span-1 text-muted-foreground text-sm">{t('bot_id')}:</div>
            <div className="col-span-2 text-sm font-mono">{truncatedBotId}</div>
            <div className="col-span-1 text-muted-foreground text-sm">{t('description')}:</div>
            <div className="col-span-2 flex items-start gap-1">
              <div className="text-sm">{truncatedDescription}</div>
              {isLongDescription && (
                <Button 
                  onClick={() => setShowFullDescription(true)} 
                  variant="ghost" 
                  size="sm" 
                  className="p-0 h-auto"
                  aria-label={t('description')}
                >
                  <Eye size={16} className="text-muted-foreground hover:text-foreground" />
                </Button>
              )}
            </div>
            {exchanges && exchanges.length > 0 && (
              <>
                <div className="col-span-1 text-muted-foreground text-sm">{t('exchange')}:</div>
                <div className="col-span-2">
                  <div className="flex flex-wrap gap-1">
                    {exchanges.map((exchange, i) => (
                      <Badge key={i} variant="outline">{exchange}</Badge>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div className="col-span-1 text-muted-foreground text-sm">{t('last_updated')}:</div>
            <div className="col-span-2 flex items-center gap-1 text-sm">
              <Clock size={14} />
              {format(new Date(bot.last_updated), 'MMM d, yyyy HH:mm')}
            </div>
            <div className="col-span-1 text-muted-foreground text-sm">{t('created')}:</div>
            <div className="col-span-2 text-sm">
              {format(new Date(bot.created_date), 'MMM d, yyyy')}
            </div>
            <div className="col-span-1 text-muted-foreground text-sm">{t('type')}:</div>
            <div className="col-span-2">
              <Badge variant="secondary">{bot.type.replace('_', ' ')}</Badge>
            </div>
          </div>
        </CardContent>
        <CardFooter className="pt-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowEditDialog(true)}
          >
            {t('edit_bot')}
          </Button>
        </CardFooter>
      </Card>

      <Dialog open={showFullDescription} onOpenChange={setShowFullDescription}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('bot_description')}</DialogTitle>
            <DialogDescription>
              {t('full_description_for', { botName: bot.name })}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 text-sm">
            {bot.description}
          </div>
        </DialogContent>
      </Dialog>
      
      {bot && (
        <EditBotDialog 
          isOpen={showEditDialog} 
          onClose={() => setShowEditDialog(false)}
          bot={bot}
        />
      )}
    </>
  );
};

